<template>
  <main v-if="page" class="layout-default">
    <component
      :is="HeaderModules[page.headerModule.__typename]"
      v-if="page.headerModule"
      class="header"
      :data="page.headerModule"
      :data-module="page.headerModule.__typename"
    >
    </component>
    <template v-for="module in page.contentModules">
      <component
        :is="ContentModules[module.__typename]"
        v-if="ContentModules[module.__typename]"
        :key="module.id"
        class="module"
        :data="module"
        :data-module="module.__typename"
      >
      </component>
    </template>
  </main>
</template>

<script setup lang="ts">
import { ContentModules } from '~/components/content-modules/ContentModules';
import { HeaderModules } from '~/components/header-modules/HeaderModules';
import { useSeo } from '~/utils/useSeo';
import type { GetPageByTranslatedSlugQuery } from '#gql';
import { useInternalRoute } from '~/datocms/useInternalRoute';

const route = useRoute();
const slug = String(route.params.slug[route.params.slug.length - 1]);

const i18n = useI18n();

const { data, error } = await useFetch<GetPageByTranslatedSlugQuery>('/api/content/page', {
  query: {
    slug,
    locale: i18n.localeProperties.value.siteLocale,
  },
});

const page = data?.value?.page ?? null;

if (!page || error.value) {
  showError({
    statusCode: 404,
  });
} else if (page) {
  const setI18nParams = useSetI18nParams();
  setI18nParams(
    page._allTranslatedSlugLocales?.reduce((acc, ts) => {
      const parentSlug = page.parent?._allTranslatedSlugLocales?.find((s) => s.locale === ts.locale)?.value;
      return {
        ...acc,
        ...(ts.locale &&
          ts.value && { [ts.locale]: { slug: parentSlug ? `${parentSlug}/${ts.value}` : `${ts.value}` } }),
      };
    }, {}) ?? {},
  );

  useSeo(page._seoMetaTags ?? [], {
    customCanonicalUrl: page.canonicalUrl
      ? `${i18n.baseUrl.value.replace(/\/$/, '')}${useInternalRoute(page.canonicalUrl, i18n.locale.value as 'de' | 'en').path.value}`
      : undefined,
  });

  route.meta.pageLink = {
    __typename: page.__typename,
    _allTranslatedSlugLocales: page._allTranslatedSlugLocales,
    parent: page.parent ? { _allTranslatedSlugLocales: page.parent._allTranslatedSlugLocales } : null,
  };
}
</script>

<style scoped lang="scss">
.layout-default {
  width: 100%;
  display: grid;
  place-items: center;
}
</style>
